import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PlayIcon,
  DocumentTextIcon,
  ChevronDownIcon,
  UserPlusIcon,
  ShareIcon
} from '@heroicons/react/24/outline';
import InviteUserModal from './InviteUserModal';
import { Assessment } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { Menu, Transition } from '@headlessui/react';
import { assessmentService } from '../services/assessmentService';
import toast from 'react-hot-toast';

interface MaturityAnalysis {
  overallScore: number;
  overallPercentage: number;
  dimensionScores: Array<{
    dimension: string;
    score: number;
    maxScore: number;
    percentage: number;
    answeredCount: number;
    questionCount: number;
  }>;
  strengths: string[];
  weaknesses: string[];
  recommendations: string[];
}

interface AssessmentActionHeaderProps {
  assessment: Assessment;
  maturityAnalysis: MaturityAnalysis | null;
  onAddNotes: () => void;
  onStatusChange?: () => void;
}

const AssessmentActionHeader: React.FC<AssessmentActionHeaderProps> = ({
  assessment,
  maturityAnalysis,
  onAddNotes,
  onStatusChange
}) => {
  const { user } = useAuth();
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  // Check if current user is an advisor or super admin
  // Only they can change status
  const normalizedRole = user?.role?.toLowerCase();
  const canChangeStatus = normalizedRole === 'advisor' ||
    normalizedRole === 'super_admin' ||
    normalizedRole === 'admin' ||
    user?.permissions?.includes('assessments:write');

  // Check if assessment is fully completed
  const isFullyCompleted = maturityAnalysis ?
    maturityAnalysis.dimensionScores.every(d => d.answeredCount === d.questionCount) : false;

  const handleStatusUpdate = async (newStatus: string) => {
    if (newStatus === 'COMPLETED' && !isFullyCompleted) {
      toast.error('Cannot mark as completed until all questions are answered.');
      return;
    }

    setIsUpdatingStatus(true);
    try {
      await assessmentService.updateAssessment(assessment.id, {
        status: newStatus as any
      });
      toast.success(`Status updated to ${getStatusText(newStatus)}`);
      if (onStatusChange) onStatusChange();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-success-100 text-success-800 border-success-200';
      case 'IN_PROGRESS':
        return 'bg-info-100 text-info-800 border-info-200';
      case 'IN_REVIEW':
        return 'bg-warning-100 text-warning-800 border-warning-200';
      case 'NEW':
        return 'bg-neutral-100 text-neutral-800 border-neutral-200';
      case 'ARCHIVED':
        return 'bg-accent-100 text-accent-800 border-accent-200';
      default:
        return 'bg-neutral-100 text-neutral-800 border-neutral-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'Completed';
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'IN_REVIEW':
        return 'In Review';
      case 'NEW':
        return 'New';
      case 'ARCHIVED':
        return 'Archived';
      default:
        return status || 'Unknown';
    }
  };

  return (
    <div className="bg-white border-b border-neutral-200 shadow-sm">
      <div className="px-4 sm:px-6 py-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          {/* Evaluation Info */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 min-w-0">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-neutral-900 break-words">
                {assessment.questionnaire?.name || 'Evaluation'}
              </h1>
              <p className="text-sm text-neutral-500 break-words">
                {assessment.client?.name || 'Unknown Client'} • Evaluation #{assessment.id}
              </p>
            </div>

            {/* Status Dropdown */}
            <div className="relative">
              {!canChangeStatus ? (
                <div className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(assessment.status)}`}>
                  {getStatusText(assessment.status)}
                </div>
              ) : (
                <Menu as="div" className="relative inline-block text-left">
                  <Menu.Button
                    disabled={isUpdatingStatus}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${getStatusColor(assessment.status)} ${isUpdatingStatus ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}
                  >
                    {getStatusText(assessment.status)}
                    <ChevronDownIcon className="ml-1 h-3 w-3" aria-hidden="true" />
                  </Menu.Button>

                  <Transition
                    as={React.Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="origin-top-left absolute left-0 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                      <div className="py-1">
                        {/* IN_PROGRESS is always an option to move to, unless we are already there. 
                              However, if IN_REVIEW, only Advisors+ can move back to IN_PROGRESS. */}
                        {(!['IN_REVIEW'].includes(assessment.status) ||
                          (user?.role && ['advisor', 'ADVISOR', 'admin', 'ADMIN', 'super_admin', 'SUPER_ADMIN'].includes(user.role))) && (
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  onClick={() => handleStatusUpdate('IN_PROGRESS')}
                                  className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} block px-4 py-2 text-sm w-full text-left`}
                                >
                                  In Progress
                                </button>
                              )}
                            </Menu.Item>
                          )}

                        {/* IN_REVIEW option */}
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => handleStatusUpdate('IN_REVIEW')}
                              className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} block px-4 py-2 text-sm w-full text-left`}
                            >
                              In Review
                            </button>
                          )}
                        </Menu.Item>

                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => handleStatusUpdate('COMPLETED')}
                              disabled={!isFullyCompleted}
                              className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                } ${!isFullyCompleted ? 'opacity-50 cursor-not-allowed' : ''} block px-4 py-2 text-sm w-full text-left`}
                              title={!isFullyCompleted ? 'All questions must be answered first' : ''}
                            >
                              Completed
                            </button>
                          )}
                        </Menu.Item>

                        {/* ARCHIVED only for Adms */}
                        {(user?.role && ['admin', 'ADMIN', 'super_admin', 'SUPER_ADMIN'].includes(user.role)) && (
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => handleStatusUpdate('ARCHIVED')}
                                className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                  } block px-4 py-2 text-sm w-full text-left`}
                              >
                                Archived
                              </button>
                            )}
                          </Menu.Item>
                        )}
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-stretch gap-2 w-full xl:w-auto">
            {/* Continue/View Evaluation */}
            {assessment.status === 'IN_PROGRESS' || assessment.status === 'NEW' ? (
              <Link
                to={`/assessments/${assessment.id}/questionnaire`}
                className="inline-flex flex-1 sm:flex-none items-center justify-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors duration-200 min-w-[9.5rem]"
              >
                <PlayIcon className="w-4 h-4 mr-2" />
                <span className="sm:hidden">Continue</span>
                <span className="hidden sm:inline">Continue Evaluation</span>
              </Link>
            ) : (
              <Link
                to={`/assessments/${assessment.id}/questionnaire`}
                className="inline-flex flex-1 sm:flex-none items-center justify-center px-4 py-2 border border-neutral-300 text-neutral-700 text-sm font-medium rounded-lg hover:bg-neutral-50 transition-colors duration-200 min-w-[9.5rem]"
              >
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                <span className="sm:hidden">Evaluation</span>
                <span className="hidden sm:inline">View Evaluation</span>
              </Link>
            )}

            {/* Maturity Analysis */}
            <Link
              to={`/assessments/${assessment.id}/maturity`}
              className="inline-flex flex-1 sm:flex-none items-center justify-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors duration-200 min-w-[9.5rem]"
            >
              <DocumentTextIcon className="w-4 h-4 mr-2" />
              <span className="sm:hidden">Analysis</span>
              <span className="hidden sm:inline">View Analysis</span>
            </Link>



            {/* Share / Invite */}
            {canChangeStatus && (
              <button
                onClick={() => setIsInviteModalOpen(true)}
                className="inline-flex flex-1 sm:flex-none items-center justify-center px-4 py-2 border border-neutral-300 text-neutral-700 text-sm font-medium rounded-lg hover:bg-neutral-50 transition-colors duration-200 min-w-[9.5rem]"
              >
                <ShareIcon className="w-4 h-4 mr-2" />
                Share
              </button>
            )}


          </div>
        </div>
      </div>
      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        assessmentId={assessment.id}
        clientId={assessment.clientId}
        clientName={assessment.client?.name || 'Unknown Client'}
      />
    </div>
  );
};

export default AssessmentActionHeader;
